import type { WidgetDelivery, WidgetManifest, WidgetProvider, WidgetResolution } from '@resto/contracts';

/**
 * mountWidget строит URL через `new URL(resolution.htmlUrl)`, то есть требует
 * абсолютный адрес. Bundled-реестр хранит пути от корня (`/widgets/<id>/...`),
 * поэтому перед монтированием приводим их к абсолютным.
 */
export interface WidgetProviders {
  remote: WidgetProvider;
  bundled: WidgetProvider;
}

function toAbsolute(url: string): string {
  return new URL(url, window.location.origin).toString();
}

export class AbsoluteWidgetsProvider implements WidgetProvider {
  readonly kind: WidgetDelivery;

  constructor(private readonly inner: WidgetProvider) {
    this.kind = inner.kind;
  }

  async resolve(manifest: WidgetManifest): Promise<WidgetResolution> {
    const resolution = await this.inner.resolve(manifest);
    return {
      ...resolution,
      htmlUrl: toAbsolute(resolution.htmlUrl),
      moduleUrl: resolution.moduleUrl ? toAbsolute(resolution.moduleUrl) : undefined,
    };
  }
}

/**
 * Dev-обёртка: реестр отдаёт entryUrl с CDN (WIDGETS_BASE_URL, :8088),
 * а локально те же файлы лежат в public/widgets. Если задан
 * VITE_WIDGETS_BASE_URL — подменяем origin, манифест при этом не меняется.
 */
export class LocalWidgetsProvider implements WidgetProvider {
  readonly kind: WidgetDelivery;

  constructor(
    private readonly inner: WidgetProvider,
    private readonly baseUrl: string,
  ) {
    this.kind = inner.kind;
  }

  async resolve(manifest: WidgetManifest): Promise<WidgetResolution> {
    const resolution = await this.inner.resolve(manifest);
    const base = toAbsolute(this.baseUrl).replace(/\/+$/, '');
    return {
      ...resolution,
      htmlUrl: `${base}/${manifest.id}/index.html`,
      moduleUrl: `${base}/${manifest.id}/index.js`,
    };
  }
}
